import React, { useEffect, useState } from "react"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { Box, FormControl, InputLabel, LinearProgress, MenuItem, Select, SelectChangeEvent, SelectProps, TextField } from "@mui/material"
import { GridSortModel } from "@mui/x-data-grid"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { StateReportBillingList } from "../components/Lists/StateReportBillingList"
import { StateReportsBillingModel } from "../models/StateReportsBillingModel"
import { apiClient } from "../helper/api"
import useAuth from "../context/AuthProvider"

export enum StateReportTabs {
  Upcoming = 1,
  Completed = 2,
  Outstanding = 3
}

export const StateReportsBilling = () => {
  const { user } = useAuth()
  const token = user?.token
  const [selectedTab, setSelectedTab] = useState<number>(1);
  const [filterModel, setFilterModel] = useState<{ items: { columnField: string, value: string, operatorValue: string }[] }>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'dueDate', sort: 'asc' }
  ]);
  const [itemList, setItemList] = useState<StateReportsBillingModel[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [loading, setloading] = useState(false);
  const initialUserdata = { partnersId: 0, customerName: "" };
  const [partners, setPartners] = useState([initialUserdata]);
  const [selectedPartner, setSelectedPartner] = useState<string>('0');
  const [totalCount, setTotalCount] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState<string>((new Date().getMonth() + 1).toString());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  

  const fetchData = async () => {
    const data = await fetchStateReportBillingData(pageSize, currentPage);
    setItemList(data);
  };

  const fetchStateReportBillingData = async (_pageSize: number, _currentPage: number) => {
    setloading(true);
    
    // Calculate dates based on business logic for each tab
    let month = selectedMonth;
    let year = selectedYear;
    
    // Business Logic Implementation:
    // All tabs: Use selected month/year directly
    // If user selects September, show data for September
    month = selectedMonth;
    year = selectedYear;
    
    // Debug logging
    console.log('🔍 STATE REPORTS BILLING API CALL:', {
      selectedTab: selectedTab,
      tabName: selectedTab === 1 ? 'Upcoming' : selectedTab === 2 ? 'Completed' : selectedTab === 3 ? 'Outstanding' : 'Unknown',
      originalMonth: selectedMonth,
      originalYear: selectedYear,
      calculatedMonth: month,
      calculatedYear: year,
      selectedPartner: selectedPartner
    });
    
    // Create request body object (simplified - no date range)
    const requestBody = {
      pageSize: _pageSize || 50,
      currentPage: _currentPage || 1,
      PartnerId: selectedPartner || '',
      month: month,
      year: year,
      field: sortModel?.[0]?.['field'] || '',
      sort: sortModel?.[0]?.['sort'] || '',
      fieldName: filterModel?.items?.[0]?.['columnField'] || '',
      value: filterModel?.items?.[0]?.['value'] || '',
      operator: filterModel?.items?.[0]?.['operatorValue'] || '',
      selectedTab: selectedTab,
      userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };



    let data: StateReportsBillingModel[] = []
    await apiClient(`/StateReport/getStateReportBilling`, {
      method: "POST",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
      body: JSON.stringify(requestBody),
    })
    .then(responseJson => {
      if (responseJson){
        setTotalCount(responseJson.total);
        data = responseJson.data;
      } else {
        data = [];
      }
    })
    .finally(() => {
      setloading(false);
    });
    return data;
  };


  const fetchPartners = () => {
    apiClient("/Partners/getPartners", {
      method: "GET",
      headers: new Headers({
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      }),
    })
    .then((responseJson) => {
      if (!responseJson) return
      setPartners(responseJson.data);
    })
  };

  useEffect(() => {
    fetchPartners()
  }, [])

  useEffect(() => {
    setloading(true);
    setSelectedPartner('0');
    setSelectedMonth((new Date().getMonth() + 1).toString());
    setSelectedYear(new Date().getFullYear().toString());
    setFilterModel({ items: [] });
    setSortModel([]);
    setCurrentPage(1);
    setPageSize(25);
    setTotalCount(0);
    setItemList([]);
    setloading(false);
}, [selectedTab]);

  useEffect(() => {
    // Always fetch data when dependencies change
    fetchData();
  }, [selectedPartner, selectedMonth, selectedYear, pageSize, currentPage, filterModel, sortModel])
  
  const FMSelectCustomCustomer: React.FC<SelectProps> = ({ variant, ...props }) => {
    const handleChangePartner = (event: SelectChangeEvent) => {
      setSelectedPartner(event.target.value.toString())
    };
    return (
      <div>
        <FormControl fullWidth>
          <Select
            onChange={handleChangePartner}
            value={selectedPartner}
            id="test-select-label"
            autoWidth
            size="small"
            label={null}
            className="border-round custom-menu-item"
            IconComponent={ExpandMoreIcon}
            displayEmpty
          >
            <MenuItem key={""} value={"0"}>All</MenuItem>
            {partners?.map((item, key) => (
              <MenuItem
                key={key}
                className="custom-menu-item"
                value={item.partnersId}
              >
                {item.customerName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    );
  };


  function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: '100%', height: '100%' }}>
        <div className="card-title d-flex col-lg-12">
          <span className="col-lg-5">State Reports Billing</span>
          <div className="col-lg-2">
            {(user?.userRoles.split(",").includes("Super Admin") || user?.userRoles.split(",").includes("MISC State Reporter")) ? (
              <FMSelectCustomCustomer name="customer" />
            ) : ""}
          </div>
          {/* Simple Month/Year Controls (like MonthEndBillingModule) */}
          <FormControl fullWidth className="main-form-2 ms-3 d-flex">
            <InputLabel className="" id="month-Label">Select Month</InputLabel>
            <Select
              labelId="month-Label"
              IconComponent={ExpandMoreIcon}
              label="Select Month"
              value={selectedMonth}
              onChange={(event: SelectChangeEvent<string>) => setSelectedMonth(event.target.value)}
            >
              <MenuItem value={1}>January</MenuItem>
              <MenuItem value={2}>February</MenuItem>
              <MenuItem value={3}>March</MenuItem>
              <MenuItem value={4}>April</MenuItem>
              <MenuItem value={5}>May</MenuItem>
              <MenuItem value={6}>June</MenuItem>
              <MenuItem value={7}>July</MenuItem>
              <MenuItem value={8}>August</MenuItem>
              <MenuItem value={9}>September</MenuItem>
              <MenuItem value={10}>October</MenuItem>
              <MenuItem value={11}>November</MenuItem>
              <MenuItem value={12}>December</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth className="main-form-3 ms-3 d-flex">
            <TextField
              label="Year"
              type="number"
              value={selectedYear}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSelectedYear(event.target.value)}
              defaultValue={new Date().getFullYear()}
              InputProps={{
                style: { height: '2.8rem' }
              }}
            />
          </FormControl>
        </div>

      <div style={{ position: "fixed", top: "0", left: "0", zIndex: "9990", width: "100%" }}>
        {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
      </div>
      <Box sx={{ width: "100%" }}>
        <div className="col-lg-12    ">
          <div
            className=" d-flex  "
            style={{ marginBottom: "28px" }} role="group"
            aria-label="Basic example"
          >
            <React.Fragment>
                        <div className="col-4  text-center ">
                          <button type="button"
                            className={`btn-tab btn ${selectedTab === 1 ? "btn-Selected" : "btn-primary1"
                              }`}
                            {...a11yProps(8)}
                            onClick={() => setSelectedTab(1)}
                          >
                           Upcoming Report
                          </button>
                        </div>
                        <div className="col-4 text-center">
                            <button
                                type="button"
                                
                                className={`btn-tab btn ${selectedTab === 2 ? "btn-Selected" : "btn-primary"
                                }`}
                                {...a11yProps(5)}
                                onClick={() => setSelectedTab(2)}
                            >
                                Completed Report
                            </button>
                        </div>
                        <div className="col-4 text-center">
                            <button
                                type="button"
                                
                                className={`btn-tab btn ${selectedTab === 3 ? "btn-Selected" : "btn-primary"
                                }`}
                                {...a11yProps(5)}
                                onClick={() => setSelectedTab(3)}
                            >
                                Outstanding Report
                            </button>
                        </div>
            </React.Fragment>
          </div>
        </div>
      </Box>
          <StateReportBillingList
            listData={itemList}
            setItemList={setItemList}
            setFilterModel={setFilterModel}
            setSortModel={setSortModel}
            sortModel={sortModel}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            selectedTab={selectedTab}
            pageSize={pageSize}
            setPageSize={setPageSize}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalCount={totalCount}
            selectedPartner={selectedPartner}
            fetchStateReportBillingData={fetchStateReportBillingData}
          />
      </Box>
    </LocalizationProvider>
  )
}
